import { Field, InjectedFormProps } from 'redux-form';
import {
  IChannel,
  IOptionMap,
  IPremium,
  IProduct,
  IQuotes,
  ITraveller,
  TripType
} from '../../../../../data/api/models';
import { II18nMessages } from '../../../../../services/branding/models';
import { Collapsable, ICollapsableComponent, IQuoteActions, IQuoteState } from '../../../../../services/quote/models';
import { standardiseTheName } from '../../../../../helpers/Quote';
import { find, get, head, isEmpty, keys, map, reduce, size } from 'lodash';
import Loading from '../../../../../components/Loading';
import { isRequired } from '../../../../../util/validators';
import { ButtonAsRadio } from '../../../../../components/ButtonRadioStyle';
import { FormattedNumber } from 'react-intl';
import Benefit from './Benefit';
import DefinitionPopup from '../../../../../components/DefinitionPopup';
import { Upsell } from './Upsell';
import { windowSize } from '../../../../../hooks/windowSize';
import FieldFormControl from '../../../../../components/FieldFormControl';
import DataLayer from '../../../../../helpers/DataLayer';
import { setCollapsable } from '../../../../../services/quote/reducer';
import { Button, Col, Row } from 'react-bootstrap';
import { AdmiralCardV2 } from '../AdmiralCardV2';
import React = require('react');

interface ISchemeChoiceProps extends InjectedFormProps {
  tripType: TripType;
  quotes: IQuotes;
  schemeId: string,
  options: IOptionMap,
  product: IProduct;
  premium: IPremium;
  i18n: II18nMessages;
  synchronousError: any;
  quoteState: IQuoteState;
  recalculate: () => any;
  maxTripDuration: any;
  startDate: string;
  region: string;
  endDate: string;
  channel: IChannel;
  travellers: ITraveller[];
  change: any;
  valid: boolean;
  collapsable?: ICollapsableComponent;
  actions: {
    dispatch: any;
    quote: IQuoteActions;
  }

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

interface IBenefit {
  title: string;
  shortName: string;
  displayName: string;
  subBenefit?: string;
}


export const SchemeChoice = ({
                               product,
                               quoteState,
                               travellers,
                               region,
                               quotes,
                               tripType,
                               maxTripDuration,
                               change,
                               renderDefinition,
                               i18n,
                               schemeId,
                               startDate,
                               premium,
                               endDate,
                               channel,
                               actions,
                               recalculate,
                               valid,
                               collapsable,
                               ...props
                             }: ISchemeChoiceProps) => {

  const BENEFITS: IBenefit[] = [
    { title: 'Medical expenses', shortName: 'Medical Expenses', displayName: 'Medical' },
    { title: 'Trip cancellation', shortName: 'Trip Cancellation', displayName: 'Cancellation' },
    { title: 'Personal belongings', shortName: 'Personal Belongings', displayName: 'Baggage' },
    {
      title: 'Overall valuables limit',
      shortName: 'Personal Belongings',
      displayName: 'Overall valuables limit',
      subBenefit: 'valuables total limit'
    },
    {
      title: 'Single item limit',
      shortName: 'Personal Belongings',
      displayName: 'Single item limit',
      subBenefit: 'single item limit'
    },
    { title: 'Money and documents', shortName: 'Money & Documents', displayName: null },
    { title: 'Excess per traveller', shortName: null, displayName: 'Policy Excess' },
  ]

  const findBenefit = (benefitToMatch: IBenefit, benefits: any[]) => {
    const benefit = find(benefits, ['shortName', benefitToMatch.shortName]);
    if (benefitToMatch.subBenefit) return find(benefit.subBenefits, ['name', benefitToMatch.subBenefit]);
    else return benefit;
  }

  const isMobile = windowSize(480).isMobile;

  const dispatchClaimRates = () => {

    const quotes = quoteState.quotes;
    const coverLevel = keys(quotes)[0];
    const tripType = keys(quotes[coverLevel])[0];
    const quotedCover = quotes[coverLevel][tripType];
    const information = quotedCover.information;
    const claimCalculated = {
      perilName: get(information, 'claimCalculated.perilName', 'other'),
      claimCount: get(information, 'claimCalculated.claimCount', 0),
      claimRate: get(information, 'claimCalculated.claimRate', 1),
    };
    if (!get(quoteState, 'application.claimCalculated', null)) {
      change('claimCalculated', claimCalculated);
    }
  }

  const onSchemeSelected = (selectedSchemeId: string) => {
    DataLayer.tierUpgrade(get(props, 'premiums'), schemeId, selectedSchemeId)
    Promise.resolve(change('selectedSchemeId', selectedSchemeId)).then(() => {
      Promise.resolve(change('schemeId', selectedSchemeId)).then(() => {
        recalculate();
      })
    })
  }

  const isSelected = (quoteSchemeId) => {
    return quoteSchemeId === schemeId;
  }

  const renderBenefitDescriptions = (benefitToMatch: IBenefit, quote) => {

    const benefits = Benefit.getBenefits(quote)
    const benefit = findBenefit(benefitToMatch, benefits);
    let description;

    switch (benefitToMatch.title) {
      case 'Excess per traveller':
        description = get(product, 'metadata.definitions.excess')
        break;
      default:
        description = get(benefit, 'description')
    }

    return (
      <div className={'benefit-title-container'}>
        <div className={'benefit-title'}>{benefitToMatch.title}</div>
        <div className={'benefit-definition'}>
          <DefinitionPopup
            name="benefit-info"
            definition={description}
            iconType="info"
          />
        </div>
      </div>
    )
  }

  const renderBenefitsSection = (selectedTripTypeQuotes) => {
    const quoteForBenefits = head(selectedTripTypeQuotes);

    return map(BENEFITS, (benefit) => {
      return (
        <div className={'scheme-selection-container'}>
          <div
            className={'scheme-choice-header-title sub'}>{renderBenefitDescriptions(benefit, quoteForBenefits)}</div>
          {map(selectedTripTypeQuotes, (quote, index) => {
            const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
            return (
              <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
                <Benefit
                  quote={quote}
                  shortName={benefit.shortName}
                  displayName={benefit.displayName}
                />
              </div>
            )
          })}
        </div>
      )
    })
  }

  const onChangeCollapse = () => {
    actions.dispatch(setCollapsable(null, Collapsable.SCHEME_CHOICE));
  }

  const renderSchemeSelection = (selectedTripTypeQuotes) => {
    const disabled = false;
    let hasDiscount = false;

    return (
      <div className={'scheme-selection-container'}>
        <div className={'scheme-choice-header-title'}/>
        {map(selectedTripTypeQuotes, (quote, index) => {
          const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
          hasDiscount = get(quote, 'discount') < 0;
          const discountedPrice = get(quote, 'premiumBeforeAdjustment.gross') ? get(quote, 'premiumBeforeAdjustment.gross').toFixed(2) : 0.00;
          const schemeId = get(quote, 'scheme.id');
          return (
            <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
              <div className={'scheme-price-container'}>
                {hasDiscount && (
                  <div className={'discount-price-strikethrough'}>
                    {'£' + discountedPrice}
                  </div>
                )}
                <FormattedNumber
                  value={quote.gross}
                  style="currency"
                  currency={'GBP'}
                />
              </div>

              {!isMobile && (
                <Field
                  name="schemeId"
                  simpleValue={schemeId}
                  className={'admiral-white-btn'}
                  style={{ minWidth: '90px' }}
                  selectedText={'SELECTED'}
                  unSelectedText={'SELECT'}
                  disabled={disabled || quoteState.isCalculating}
                  component={ButtonAsRadio}
                  validate={[isRequired]}
                  handleChange={onSchemeSelected.bind(this, schemeId)}
                />
              )}

              {isMobile && (
                <div className={'scheme-choice-mobile-radio'}>
                  <Field
                    name="schemeId"
                    type="radio"
                    value={schemeId}
                    complexValue={true}
                    component={FieldFormControl}
                    validate={[isRequired]}
                    disabled={disabled || quoteState.isCalculating}
                    onChange={onSchemeSelected.bind(this, schemeId)}
                  >
                    <div/>
                  </Field>
                </div>
              )}

            </div>
          )
        })}
      </div>
    );
  }

  const renderUpsell = () => {
    return (
      <Upsell
        startDate={startDate}
        endDate={endDate}
        tripType={tripType}
        maxTripDuration={maxTripDuration}
        channel={channel}
        travellers={travellers}
        region={region}
        product={product}
        quoteState={quoteState}
        recalculate={recalculate}
        renderDefinition={renderDefinition}
        actions={actions}
        i18n={i18n}
        valid={valid}
        change={change}
        {...props}
      />
    )
  }

  const handleContinue = () => {
    actions.dispatch(setCollapsable(Collapsable.SCHEME_CHOICE, Collapsable.ADDITIONAL));
  }

  const renderContinueButton = () => {
    return (
      <Row style={{ padding: '15px' }}>
        <Col xs={12}>
          <Button
            bsSize={'small'}
            type="button"
            block={true}
            disabled={!schemeId || quoteState.isCalculating}
            onClick={handleContinue}
            className={'admiral-primary-btn'}>
            CONTINUE
          </Button>
        </Col>
      </Row>
    )
  }

  const renderQuotes = () => {
    const quotes = quoteState.quotes;
    if (isEmpty(quotes) || !product || quoteState.isCalculating) {
      return (
        <Loading/>
      )
    }

    let tripTypeName = standardiseTheName(product.metadata.trip_type[tripType]);

    const orderedQuotes = product.metadata.scheme_order.map((order) => {
      return quotes[order];
    }).filter((quotes) => !!quotes);


    if (!tripType) {
      return null;
    }
    dispatchClaimRates();

    const selectedTripTypeQuotes = map(orderedQuotes[0] && orderedQuotes, (tripTypes, level) => {
      if (size(tripTypes) === 1) {
        tripTypeName = keys(tripTypes)[0];
      }
      return tripTypes[tripTypeName]
    })

    const documentsOrder = [
      'Insurance Product Information Document',
      'Policy Wording',
    ];

    return (
      <>
        <div className={'scheme-selection-container'}>
          <div className={'scheme-choice-header-title'}>&nbsp;</div>
          {map(selectedTripTypeQuotes, (quote, index) => {
            const image = require('../../../img/' + get(quote, 'scheme.coverLevel') + '-level.svg')
            const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
            return (
              <div className={`scheme-choice-cover-levels ${isSchemeSelected}`}>
                <img src={image} alt="Admiral"/>
              </div>
            )
          })}
        </div>

        {renderSchemeSelection(selectedTripTypeQuotes)}

        {renderBenefitsSection(selectedTripTypeQuotes)}

        <div className={'scheme-selection-container'}>
          <div className={'scheme-choice-header-title sub'}>Insurance Product Information Document</div>
          {map(selectedTripTypeQuotes, (quote, index) => {
            const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
            const documents = get(quote, 'scheme.documents');
            const isAnnual = tripTypeName === 'Annual Multi Trip';
            const coverLevel = get(quote, 'scheme.coverLevel');

            const filteredDocuments = reduce(keys(documents), (acc: any, current: string) => {
              const isIpid = current.toLowerCase().includes('ipid');
              const isCoverLevel = current.toLowerCase().includes(coverLevel);
              const isSingle = current.toLowerCase().includes(tripTypeName.toLowerCase());
              const isMultiTrip = isAnnual && current.toLowerCase().includes('multi-trip');

              if (!isIpid || (isCoverLevel && (isSingle || isMultiTrip))) {
                acc[current] = documents[current];
              }

              return acc;
            }, {});

            if (quote === null) {
              return null;
            }
            return (
              <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
                {map(documentsOrder, (documentName: string) => {
                  const documentUrl = get(filteredDocuments, documentName);
                  if (!documentUrl || documentName !== 'Insurance Product Information Document') {
                    return null;
                  }

                  return (
                    <div>
                      <a className={'scheme-document-link'} href={documentUrl} target="_blank"
                         key={documentName}>
                        <img src={require('../../../img/document.svg')} alt={'Admiarl'}/>
                        <div>OPEN</div>
                      </a>
                    </div>
                  );
                })}
              </div>
            )
          })}
        </div>

        {renderSchemeSelection(selectedTripTypeQuotes)}

        {renderUpsell()}
      </>

    )
  }

  return (
    <AdmiralCardV2
      title={'Which level of cover do you need?'}
      icon={'shield-ticked.svg'}
      showHeaderWhenOpen={true}
      collapsable={{
        value: collapsable?.schemeSelection || { opened: true, isEditable: true }, // either handled by collapsable (scheme select page) OR always open (QuoteSummary page)
        onHandleChange: onChangeCollapse
      }}
      styleBody={{ padding: '0' }}
    >
      {renderQuotes()}
      {collapsable && renderContinueButton()}
    </AdmiralCardV2>
  )
}
